/**
 * Data Migration — TWO modes:
 *
 *   1. IMPORT       → Excel / CSV / ZIP (existing importer bridge)
 *   2. MANUAL       → paper-file receptionist workspace
 *
 * Manual mode is a single-screen split-panel:
 *   LEFT  — patient search + quick-create (persistent selection)
 *   RIGHT — tabbed record forms (Reports · Treatments · Bills · Appointments · Documents)
 *
 * The workflow rule: the receptionist never leaves this page. After every
 * save the record form clears itself but the SELECTED PATIENT stays intact.
 * Selection + last-active-tab persist in localStorage so a mid-session
 * reload doesn't force them to re-enter the current patient.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Database, Upload, EyeOff, Search, UserPlus, X, FileSpreadsheet,
  FlaskConical, Stethoscope, Receipt, CalendarDays, FileText, Check, Users,
} from "lucide-react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { useWorkspace } from "@/lib/workspace";
import { Time24Input } from "@/components/Time24Input";
import { LAB_MATERIALS } from "@/components/app/LabCasesCard";
import { DateDMYInput } from "@/components/DateDMYInput";

const LS_PATIENT_KEY = "viyora.migration.selectedPatient";
const LS_TAB_KEY     = "viyora.migration.tab";
// Migration counters live in localStorage so opening a sub-form, navigating
// away from the workspace, or refreshing the browser never resets them.
// The counters represent "records migrated in this session" and only clear
// on explicit user action (the Reset button on the progress strip).
const LS_STATS_KEY   = "viyora.migration.stats";
const EMPTY_STATS    = { patients: 0, reports: 0, treatments: 0, prescriptions: 0,
                         lab_cases: 0, bills: 0, appointments: 0, documents: 0 };

export default function DataMigration() {
  const { user } = useAuth();
  const workspace = useWorkspace();
  const [mode, setMode] = useState("manual");   // "import" | "manual"

  const disableModule = async () => {
    if (!window.confirm("Hide the Data Migration module from the sidebar? Your imported data stays inside patient profiles.")) return;
    try {
      await api.post("/workspace/data-migration/toggle", { enabled: false });
      await workspace.refresh();
      toast.success("Data Migration hidden — re-enable from Settings → Workspace.");
    } catch (e) { toast.error(apiError(e, "Could not disable module")); }
  };

  return (
    <div className="space-y-6" data-testid="data-migration-workspace">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="overline text-ink-soft">Onboarding utility</div>
          <h1 className="mt-2 font-display text-4xl tracking-tight">Data Migration</h1>
          <p className="mt-2 text-sm text-ink-mid max-w-2xl">
            Two paths, one destination — every record ends up inside the right patient's profile and timeline automatically.
          </p>
        </div>
        {user?.role === "admin" && (
          <button
            onClick={disableModule}
            data-testid="dm-disable-module"
            className="inline-flex items-center gap-1.5 text-xs text-ink-mid hover:text-[#B94A48] border border-[#E8EAE9] hover:border-[#EDC7C6] rounded-full px-3 py-1.5"
          >
            <EyeOff className="w-3 h-3" /> Hide module
          </button>
        )}
      </div>

      {/* Mode toggle */}
      <div className="inline-flex rounded-full border border-[#E8EAE9] p-1 bg-white text-sm" data-testid="dm-mode-toggle">
        {[
          { id: "manual", label: "Manual Migration", desc: "Paper files, single receptionist workspace" },
          { id: "import", label: "Import",           desc: "Excel · CSV · Paste from Excel" },
        ].map((m) => (
          <button key={m.id} onClick={() => setMode(m.id)}
                  data-testid={`dm-mode-${m.id}`}
                  className={`px-4 py-2 rounded-full transition-all ${
                    mode === m.id ? "bg-pine text-linen shadow-sm" : "text-ink-mid hover:text-ink"
                  }`}>
            {m.label}
          </button>
        ))}
      </div>

      {mode === "import" ? <ImportMode /> : <ManualMode />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// IMPORT MODE — bridges to the existing CSV/ZIP importer
// ─────────────────────────────────────────────────────────────────────────────
function ImportMode() {
  return (
    <div className="space-y-4" data-testid="dm-import-mode">
      <div className="rounded-2xl border border-[#E8EAE9] bg-white p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-pine/10 text-pine grid place-items-center">
            <FileSpreadsheet className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="font-medium">Excel / CSV / ZIP import</div>
            <div className="text-sm text-ink-mid mt-1">
              Supports Practo, Dentrix, CareStack, or any spreadsheet that follows the Viyora template.
              Auto-detects patient rows, links attached files, and flags duplicates.
            </div>
            <Link
              to="/app/settings/data/import"
              data-testid="dm-open-legacy-import"
              className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-pine text-linen px-4 py-2 text-sm font-medium hover:bg-pine/90"
            >
              <Upload className="w-4 h-4" /> Open importer
            </Link>
          </div>
        </div>
      </div>
      <div className="rounded-2xl border border-dashed border-[#E8EAE9] bg-linen/40 p-5 text-sm text-ink-mid">
        Coming next: paste-from-Excel spreadsheet UI directly here for Patients, Reports, Treatments, Bills and Appointments (no file needed).
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MANUAL MODE — split-panel workspace with Quick-Add buttons + Timeline preview
// ─────────────────────────────────────────────────────────────────────────────
function ManualMode() {
  const [patient, setPatientState] = useState(null);
  const [activeForm, setActiveForm] = useState(null);   // null | reports | treatments | ...
  // Hydrate counters from localStorage synchronously so the strip never
  // flashes 0 on remount / navigation.
  const [stats, setStats] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_STATS_KEY);
      if (raw) return { ...EMPTY_STATS, ...JSON.parse(raw) };
    } catch { /* ignore */ }
    return { ...EMPTY_STATS };
  });
  const [timelineTick, setTimelineTick] = useState(0);  // bump to force timeline refresh

  const setPatient = (p) => {
    setPatientState(p);
    setActiveForm(null);
    try {
      if (p) localStorage.setItem(LS_PATIENT_KEY, JSON.stringify({ id: p.id, name: p.name, phone: p.phone }));
      else localStorage.removeItem(LS_PATIENT_KEY);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_PATIENT_KEY);
      if (raw) setPatientState(JSON.parse(raw));
    } catch { /* ignore */ }
  }, []);

  // Every time stats change, mirror to localStorage so the value survives
  // page unmount / route change / hard refresh.
  useEffect(() => {
    try { localStorage.setItem(LS_STATS_KEY, JSON.stringify(stats)); }
    catch { /* ignore quota errors */ }
  }, [stats]);

  const bump = (kind) => {
    setStats((s) => ({ ...s, [kind]: (s[kind] || 0) + 1 }));
    setTimelineTick((t) => t + 1);
    setActiveForm(null);   // return to patient view after each save (patient stays)
  };

  const resetStats = () => {
    if (!window.confirm("Reset all migration counters to zero? This does NOT delete any records — it only resets the progress strip for a fresh migration session.")) return;
    setStats({ ...EMPTY_STATS });
    try { localStorage.removeItem(LS_STATS_KEY); } catch { /* ignore */ }
    toast.success("Migration counters reset");
  };
  const totalMigrated = Object.values(stats).reduce((a, b) => a + (b || 0), 0);

  const QUICK_ADDS = [
    { id: "reports",       label: "Report",       icon: FlaskConical },
    { id: "treatments",    label: "Treatment",    icon: Stethoscope },
    { id: "prescriptions", label: "Prescription", icon: FileText },
    { id: "lab_cases",     label: "Lab Case",     icon: FlaskConical },
    { id: "bills",         label: "Bill",         icon: Receipt },
    { id: "appointments",  label: "Appointment",  icon: CalendarDays },
    { id: "documents",     label: "Document",     icon: FileText },
  ];

  return (
    <div className="space-y-4" data-testid="dm-manual-mode">
      {/* Progress strip */}
      <div className="flex items-center justify-between gap-3">
        <div className="text-[11px] uppercase tracking-widest text-ink-soft">
          Migration session · <span className="text-ink font-medium tabular-nums" data-testid="dm-total-migrated">{totalMigrated}</span> record{totalMigrated === 1 ? "" : "s"} saved
        </div>
        {totalMigrated > 0 && (
          <button
            onClick={resetStats}
            data-testid="dm-reset-stats"
            className="inline-flex items-center gap-1 text-[11px] font-medium text-ink-mid hover:text-rose-600 transition-colors"
            title="Reset migration counters. This does not delete any saved records."
          >
            Reset counters
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="dm-stats">
        <ProgressChip label="Patients"      value={stats.patients}      icon={Users} />
        <ProgressChip label="Reports"       value={stats.reports}       icon={FlaskConical} />
        <ProgressChip label="Treatments"    value={stats.treatments}    icon={Stethoscope} />
        <ProgressChip label="Prescriptions" value={stats.prescriptions} icon={FileText} />
        <ProgressChip label="Lab Cases"     value={stats.lab_cases}     icon={FlaskConical} />
        <ProgressChip label="Bills"         value={stats.bills}         icon={Receipt} />
        <ProgressChip label="Appointments"  value={stats.appointments}  icon={CalendarDays} />
        <ProgressChip label="Documents"     value={stats.documents}     icon={FileText} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr_320px] gap-4">
        <PatientPanel
          patient={patient}
          onSelect={setPatient}
          onQuickCreated={(p) => { setPatient(p); bump("patients"); }}
        />

        {/* CENTRE — Quick-Add buttons + active form */}
        <div className="rounded-3xl border border-[#E8EAE9] bg-white overflow-hidden min-h-[520px]">
          {!patient ? (
            <div className="p-10 text-center">
              <div className="w-12 h-12 mx-auto rounded-full bg-linen grid place-items-center">
                <UserPlus className="w-5 h-5 text-ink-soft" />
              </div>
              <h3 className="mt-4 font-display text-xl">Pick or create a patient to start</h3>
              <p className="mt-1 text-sm text-ink-mid max-w-md mx-auto">
                Every record you save on this screen attaches to the selected patient automatically.
              </p>
            </div>
          ) : (
            <div className="p-5">
              {!activeForm && (
                <div data-testid="dm-quick-add">
                  <div className="mb-2 text-sm font-medium">Add a record for <span className="text-pine">{patient.name}</span></div>
                  <div className="text-xs text-ink-soft mb-4">Tap a chip below · saved records appear in the timeline on the right</div>
                  <div className="flex flex-wrap gap-2">
                    {QUICK_ADDS.map((q) => (
                      <button key={q.id} onClick={() => setActiveForm(q.id)}
                              data-testid={`dm-quick-${q.id}`}
                              className="inline-flex items-center gap-1.5 rounded-full border border-[#E8EAE9] hover:border-pine hover:bg-pine/5 text-ink text-sm px-4 py-2 transition-colors">
                        <span className="w-5 h-5 rounded-full bg-pine text-linen grid place-items-center text-[10px]">+</span>
                        <q.icon className="w-3.5 h-3.5 text-pine" />
                        {q.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {activeForm && (
                <div>
                  <button onClick={() => setActiveForm(null)} data-testid="dm-form-back"
                          className="text-xs text-ink-mid hover:text-ink mb-3 inline-flex items-center gap-1">
                    ← Back to Quick Add
                  </button>
                  {activeForm === "reports"       && <ReportForm       patient={patient} onSaved={() => bump("reports")} />}
                  {activeForm === "treatments"    && <TreatmentForm    patient={patient} onSaved={() => bump("treatments")} />}
                  {activeForm === "prescriptions" && <PrescriptionForm patient={patient} onSaved={() => bump("prescriptions")} />}
                  {activeForm === "lab_cases"     && <LabCaseForm      patient={patient} onSaved={() => bump("lab_cases")} />}
                  {activeForm === "bills"         && <BillForm         patient={patient} onSaved={() => bump("bills")} />}
                  {activeForm === "appointments"  && <AppointmentForm  patient={patient} onSaved={() => bump("appointments")} />}
                  {activeForm === "documents"     && <DocumentForm     patient={patient} onSaved={() => bump("documents")} />}
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT — Live timeline preview */}
        <TimelinePreview patient={patient} tick={timelineTick} />
      </div>
    </div>
  );
}

function ProgressChip({ label, value, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-[#E8EAE9] bg-white px-3 py-2 flex items-center gap-2">
      <Icon className="w-4 h-4 text-pine" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-ink-soft">{label}</div>
        <div className="font-display text-lg leading-none" data-testid={`dm-stat-${label.toLowerCase()}`}>{value}</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LEFT PANEL — patient search + quick-create
// ─────────────────────────────────────────────────────────────────────────────
function PatientPanel({ patient, onSelect, onQuickCreated }) {
  const [patients, setPatients] = useState([]);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", age: "", gender: "" });
  const [saving, setSaving] = useState(false);
  // Duplicate detection — when POST /patients returns 409 with candidate
  // matches, we surface them in a modal so the receptionist can pick
  // Merge / Create-new / Skip instead of silently duplicating a patient.
  const [dupes, setDupes] = useState(null);   // { attempted, matches[] }

  const reload = () => api.get("/patients").then(r => setPatients(r.data || [])).catch(() => {});
  useEffect(() => { reload(); }, []);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return patients.slice(0, 40);
    return patients.filter(p =>
      (p.name || "").toLowerCase().includes(query) ||
      (p.phone || "").toString().includes(query)
    ).slice(0, 40);
  }, [patients, q]);

  const buildPayload = () => {
    const payload = {
      name:   form.name.trim(),
      phone:  form.phone.trim(),
      gender: form.gender || null,
    };
    const age = Number(form.age);
    if (age > 0) {
      const y = new Date().getFullYear() - age;
      payload.dob = `${y}-01-01`;   // rough — good enough for historical migration
    }
    return payload;
  };

  const doCreate = async ({ force }) => {
    const payload = buildPayload();
    setSaving(true);
    try {
      const url = force ? "/patients?force=true" : "/patients";
      const { data } = await api.post(url, payload);
      toast.success(`Patient created: ${data.name}`);
      setForm({ name: "", phone: "", age: "", gender: "" });
      setCreating(false);
      setDupes(null);
      await reload();
      onQuickCreated(data);
    } catch (e) {
      // 409 = duplicate detection tripped. FastAPI serializes
      // `raise HTTPException(detail={...})` as `{"detail": {...}}` so the
      // candidate matches live at `e.response.data.detail.matches`.
      const detail = e?.response?.data?.detail || e?.response?.data;
      if (e?.response?.status === 409 && detail?.matches?.length) {
        setDupes({ attempted: payload, matches: detail.matches });
      } else {
        toast.error(apiError(e, "Could not create patient"));
      }
    } finally { setSaving(false); }
  };

  const quickCreate = () => {
    if (!form.name.trim() || !form.phone.trim()) {
      return toast.error("Name and phone are required");
    }
    doCreate({ force: false });   // duplicate detection ON — the honest path
  };

  const chooseMerge = async (match) => {
    // The receptionist picked an existing patient — hydrate the full record
    // (the /patients duplicate payload is trimmed) and hand it up.
    setDupes(null);
    setCreating(false);
    setForm({ name: "", phone: "", age: "", gender: "" });
    try {
      const { data } = await api.get(`/patients/${match.id}`);
      onSelect(data);
      toast.success(`Merged into existing: ${data.name}`);
    } catch {
      onSelect(match);   // fallback to the trimmed match doc
      toast.success(`Merged into existing: ${match.name}`);
    }
  };

  const chooseCreateNew = () => doCreate({ force: true });
  const chooseSkip = () => { setDupes(null); /* keep the form so they can edit and retry */ };

  return (
    <aside className="rounded-3xl border border-[#E8EAE9] bg-white p-4 h-fit sticky top-4" data-testid="dm-patient-panel">
      <div className="text-[10px] uppercase tracking-widest text-ink-soft">Patient</div>

      {/* Selected patient card */}
      {patient && (
        <div className="mt-2 rounded-2xl border border-pine/30 bg-pine/5 px-3 py-2.5 flex items-center gap-2" data-testid="dm-selected-patient">
          <div className="w-8 h-8 rounded-full bg-pine text-linen grid place-items-center text-xs font-medium">
            {(patient.name || "?")[0]}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{patient.name}</div>
            <div className="text-[11px] text-ink-mid">{patient.phone || "—"}</div>
          </div>
          <button onClick={() => onSelect(null)} data-testid="dm-clear-patient"
                  className="text-ink-soft hover:text-[#B94A48]"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Search */}
      <div className="mt-3 relative">
        <Search className="w-4 h-4 text-ink-soft absolute left-3 top-3" />
        <input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Search by name or phone…"
               data-testid="dm-patient-search"
               className="w-full pl-9 pr-3 py-2 rounded-full border border-[#E8EAE9] bg-white text-sm focus:outline-none focus:border-pine" />
      </div>

      {/* Result list */}
      <div className="mt-2 border border-[#E8EAE9] rounded-2xl max-h-80 overflow-y-auto" data-testid="dm-patient-list">
        {filtered.length === 0 ? (
          <div className="px-3 py-4 text-xs text-ink-soft text-center">No matches</div>
        ) : filtered.map((p) => (
          <button key={p.id} type="button" onClick={() => onSelect(p)}
                  data-testid={`dm-patient-opt-${p.id}`}
                  className={`w-full text-left px-3 py-2 border-b border-[#F0EBE0] last:border-b-0 hover:bg-linen/60 text-sm ${
                    patient?.id === p.id ? "bg-linen" : ""
                  }`}>
            <div className="font-medium truncate">{p.name}</div>
            <div className="text-[11px] text-ink-soft">{p.phone || p.email || ""}</div>
          </button>
        ))}
      </div>

      {/* Quick-create */}
      <div className="mt-3">
        {!creating ? (
          <button onClick={() => setCreating(true)} data-testid="dm-open-quick-create"
                  className="w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-dashed border-[#E8EAE9] hover:border-pine text-ink-mid text-xs px-3 py-2">
            <UserPlus className="w-3.5 h-3.5" /> Quick create patient
          </button>
        ) : (
          <div className="rounded-2xl border border-[#E8EAE9] p-3 space-y-2" data-testid="dm-quick-create-form">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="Full name *" data-testid="dm-qc-name"
                   className="input" />
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                   placeholder="Phone *" data-testid="dm-qc-phone"
                   className="input" />
            <div className="grid grid-cols-2 gap-2">
              <input type="number" min="0" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })}
                     placeholder="Age" data-testid="dm-qc-age"
                     className="input" />
              <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}
                      data-testid="dm-qc-gender"
                      className="input">
                <option value="">Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="flex gap-2 justify-end pt-1">
              <button onClick={() => { setCreating(false); setForm({ name: "", phone: "", age: "", gender: "" }); }}
                      className="text-xs text-ink-mid hover:text-ink px-3 py-1.5">Cancel</button>
              <button onClick={quickCreate} disabled={saving} data-testid="dm-qc-save"
                      className="rounded-full bg-pine text-linen text-xs px-4 py-1.5 hover:bg-pine/90 disabled:opacity-50">
                {saving ? "Saving…" : "Save & Select"}
              </button>
            </div>
          </div>
        )}
      </div>

      <StyleShim />
      {dupes && (
        <DuplicateDialog
          attempted={dupes.attempted}
          matches={dupes.matches}
          onMerge={chooseMerge}
          onCreateNew={chooseCreateNew}
          onSkip={chooseSkip}
          saving={saving}
        />
      )}
    </aside>
  );
}

/** DuplicateDialog — Merge / Create-new / Skip decision UI.
 *  Rendered by <PatientPanel /> when POST /patients returns 409 with
 *  matched candidates. */
function DuplicateDialog({ attempted, matches, onMerge, onCreateNew, onSkip, saving }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink/40 backdrop-blur-sm p-4"
         data-testid="dm-dupe-dialog">
      <div className="w-full max-w-lg rounded-3xl bg-white border border-[#E8EAE9] shadow-2xl">
        <div className="p-5 border-b border-[#E8EAE9]">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#B94A48]">Possible match</div>
              <h3 className="mt-1 font-display text-xl">This patient may already exist</h3>
            </div>
            <button onClick={onSkip} data-testid="dm-dupe-close"
                    className="text-ink-soft hover:text-ink"><X className="w-4 h-4" /></button>
          </div>
          <div className="mt-3 rounded-2xl bg-linen/40 px-3 py-2 text-xs text-ink-mid flex flex-wrap gap-3">
            <span>You're adding:</span>
            <strong>{attempted.name}</strong>
            {attempted.phone && <span className="text-ink-soft">· {attempted.phone}</span>}
          </div>
        </div>

        <div className="p-5">
          <div className="text-xs text-ink-soft mb-2">
            {matches.length} match{matches.length > 1 ? "es" : ""} found — pick one to merge into,
            or create a new patient anyway.
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="dm-dupe-matches">
            {matches.map((m) => (
              <button key={m.id} type="button" onClick={() => onMerge(m)}
                      data-testid={`dm-dupe-merge-${m.id}`}
                      disabled={saving}
                      className="w-full text-left rounded-2xl border border-[#E8EAE9] hover:border-pine hover:bg-pine/5 px-3 py-2.5 transition-colors flex items-center gap-3 disabled:opacity-50">
                <div className="w-9 h-9 rounded-full bg-pine text-linen grid place-items-center text-xs font-medium">
                  {(m.name || "?")[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{m.name}</div>
                  <div className="text-[11px] text-ink-mid">
                    {m.phone || "—"}
                    {m.dob && <> · DOB {m.dob}</>}
                    {m.match_reasons?.length ? (
                      <span className="ml-2 text-[10px] uppercase tracking-widest text-pine">
                        Match: {m.match_reasons.join(", ")}
                      </span>
                    ) : null}
                  </div>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-pine">Merge →</span>
              </button>
            ))}
          </div>
        </div>

        <div className="border-t border-[#E8EAE9] p-4 flex flex-wrap gap-2 justify-end">
          <button onClick={onSkip} data-testid="dm-dupe-skip"
                  className="px-4 py-2 rounded-full text-sm text-ink-mid hover:bg-linen">
            Skip &amp; edit
          </button>
          <button onClick={onCreateNew} disabled={saving} data-testid="dm-dupe-create-new"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm border border-[#EDC7C6] text-[#B94A48] hover:bg-[#FCECEC] disabled:opacity-50">
            {saving ? "Creating…" : "Create new anyway"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TIMELINE PREVIEW — merges lab-cases, treatments, invoices, appointments,
// prescriptions for the selected patient and renders newest-first so the
// receptionist can verify the migration in real time.
// ─────────────────────────────────────────────────────────────────────────────
function TimelinePreview({ patient, tick }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!patient?.id) { setEvents([]); return; }
    setLoading(true);
    Promise.allSettled([
      api.get("/lab-cases", { params: { patient_id: patient.id } }),
      api.get("/treatments", { params: { patient_id: patient.id } }),
      api.get("/invoices"),
      api.get("/appointments"),
      api.get(`/patients/${patient.id}/prescriptions`),
    ]).then(([lab, tx, inv, appt, rx]) => {
      const merged = [];
      const push = (at, kind, title, sub) => at && merged.push({ at, kind, title, sub });
      (lab.value?.data || []).forEach(l => push(l.date_sent, "report",
        `${l.treatment_type || "Report"}`, l.lab_name || l.notes || ""));
      (tx.value?.data || []).forEach(t => push(t.date, "treatment",
        `${t.procedure}${t.variant ? " · " + t.variant : ""}`, t.doctor_name || ""));
      (inv.value?.data?.invoices || inv.value?.data || []).filter(i => i.patient_id === patient.id).forEach(i =>
        push(i.due_date || i.created_at, "bill",
             `Invoice ${i.invoice_number || ""}`, `₹${i.total || i.amount_total || 0} · ${i.status}`));
      (appt.value?.data || []).filter(a => a.patient_id === patient.id).forEach(a =>
        push(a.start, "appointment", a.procedure || "Appointment", a.status));
      ((rx.value?.data?.prescriptions) || []).forEach(r =>
        push(r.date || r.created_at, "prescription",
             `Rx${r.rx_number ? " #" + r.rx_number : ""}`,
             (r.items || []).map(i => i.drug || i.name).slice(0, 2).join(", ")));

      merged.sort((a, b) => (b.at || "").localeCompare(a.at || ""));
      setEvents(merged.slice(0, 25));
    }).finally(() => setLoading(false));
  }, [patient?.id, tick]);

  if (!patient) {
    return (
      <aside className="rounded-3xl border border-[#E8EAE9] bg-white p-4 h-fit sticky top-4 text-xs text-ink-soft" data-testid="dm-timeline-empty">
        <div className="text-[10px] uppercase tracking-widest">Timeline preview</div>
        <div className="mt-2">Select a patient to see their record timeline here.</div>
      </aside>
    );
  }

  const KIND_STYLE = {
    report:       { color: "#2563EB", label: "Report" },
    treatment:    { color: "#1A362B", label: "Treatment" },
    prescription: { color: "#7C3AED", label: "Rx" },
    lab_case:     { color: "#0891B2", label: "Lab Case" },
    bill:         { color: "#D06A42", label: "Bill" },
    appointment:  { color: "#059669", label: "Visit" },
  };

  return (
    <aside className="rounded-3xl border border-[#E8EAE9] bg-white p-4 h-fit sticky top-4 max-h-[80vh] overflow-y-auto" data-testid="dm-timeline">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest text-ink-soft">Timeline · {patient.name}</div>
        {loading && <div className="text-[10px] text-ink-soft">refreshing…</div>}
      </div>
      {events.length === 0 ? (
        <div className="text-xs text-ink-soft py-8 text-center">No records yet. Save one on the left to see it here.</div>
      ) : (
        <ol className="relative border-l-2 border-[#F0EBE0] ml-2 space-y-3">
          {events.map((e, i) => {
            const style = KIND_STYLE[e.kind] || { color: "#98A29B", label: "Record" };
            const key = e.id || `${e.kind || "evt"}-${e.at || i}-${(e.title || "").slice(0, 24)}`;
            return (
              <li key={key} className="pl-3 relative" data-testid={`dm-tl-${i}`}>
                <span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full border-2 border-white" style={{ background: style.color }} />
                <div className="text-[10px] uppercase tracking-widest" style={{ color: style.color }}>
                  {style.label} · {e.at ? new Date(e.at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                </div>
                <div className="text-sm font-medium">{e.title}</div>
                {e.sub && <div className="text-[11px] text-ink-mid">{e.sub}</div>}
              </li>
            );
          })}
        </ol>
      )}
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RECORD FORMS — spec-enriched field sets
// ─────────────────────────────────────────────────────────────────────────────
function ReportForm({ patient, onSaved }) {
  const [labs, setLabs] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [f, setF] = useState({
    date_sent: "", lab_id: "", treatment_type: "", test_name: "",
    findings: "", ordered_by: "", follow_up: "", has_attachment: false, notes: "",
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/laboratories").then(r => setLabs(r.data || [])).catch(() => {});
    api.get("/team").then(r => setDoctors((r.data || []).filter(t => t.role === "doctor"))).catch(() => {});
  }, []);

  const submit = async () => {
    if (!f.treatment_type.trim()) return toast.error("Report type is required");
    setSaving(true);
    try {
      // Compose all clinical detail into notes so nothing is lost — schema only
      // has one free-text field on lab_cases.
      const composed = [
        f.test_name && `Test: ${f.test_name}`,
        f.findings && `Findings: ${f.findings}`,
        f.follow_up && `Follow-up: ${f.follow_up}`,
        f.has_attachment && "Attachment available",
        f.notes,
      ].filter(Boolean).join(" · ");
      await api.post("/lab-cases", {
        patient_id: patient.id,
        lab_id:     f.lab_id || null,
        treatment_type: f.treatment_type.trim(),
        date_sent:  f.date_sent || null,
        status:     "received",
        notes:      composed || null,
      });
      toast.success("Report saved");
      setF({ ...f, date_sent: "", test_name: "", findings: "", follow_up: "", has_attachment: false, notes: "" });
      onSaved();
    } catch (e) { toast.error(apiError(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="dm-report-form">
      <SectionTitle title="Add lab report" hint="Cleared after each save · patient stays selected" />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Report date"><DateDMYInput className="input" value={f.date_sent} onChange={(e) => setF({ ...f, date_sent: e.target.value })} data-testid="dm-report-date" /></Field>
        <Field label="Laboratory">
          <select className="input" value={f.lab_id} onChange={(e) => setF({ ...f, lab_id: e.target.value })} data-testid="dm-report-lab">
            <option value="">— Select —</option>
            {labs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </Field>
        <Field label="Report type"><input className="input" value={f.treatment_type} onChange={(e) => setF({ ...f, treatment_type: e.target.value })} placeholder="CBC · X-Ray · Blood Sugar" data-testid="dm-report-type" /></Field>
        <Field label="Test name"><input className="input" value={f.test_name} onChange={(e) => setF({ ...f, test_name: e.target.value })} placeholder="Specific test" data-testid="dm-report-testname" /></Field>
        <Field label="Ordered by doctor">
          <select className="input" value={f.ordered_by} onChange={(e) => setF({ ...f, ordered_by: e.target.value })} data-testid="dm-report-orderedby">
            <option value="">— Any —</option>
            {doctors.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
          </select>
        </Field>
        <Field label="Follow-up recommendation"><input className="input" value={f.follow_up} onChange={(e) => setF({ ...f, follow_up: e.target.value })} data-testid="dm-report-followup" /></Field>
      </div>
      <Field label="Findings / result summary"><textarea rows={2} className="input resize-none" value={f.findings} onChange={(e) => setF({ ...f, findings: e.target.value })} data-testid="dm-report-findings" /></Field>
      <Field label="Remarks"><input className="input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="dm-report-notes" /></Field>
      <label className="text-xs text-ink-mid inline-flex items-center gap-2">
        <input type="checkbox" checked={f.has_attachment} onChange={(e) => setF({ ...f, has_attachment: e.target.checked })} data-testid="dm-report-attach" />
        Attachment available (upload later from patient profile)
      </label>
      <SaveButton onClick={submit} saving={saving} testid="dm-report-save" label="Save report" />
    </div>
  );
}

function TreatmentForm({ patient, onSaved }) {
  const [doctors, setDoctors] = useState([]);
  const [f, setF] = useState({
    date: "", doctor_id: "", procedure: "", tooth: "", diagnosis: "",
    status: "completed", cost: 0, notes: "", next_visit: "",
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/team").then(r => setDoctors((r.data || []).filter(t => t.role === "doctor"))).catch(() => {}); }, []);

  const submit = async () => {
    if (!f.procedure.trim()) return toast.error("Treatment is required");
    setSaving(true);
    try {
      await api.post("/treatments", {
        patient_id: patient.id,
        procedure:  f.procedure.trim(),
        tooth:      f.tooth || null,
        diagnosis:  f.diagnosis || null,
        doctor_id:  f.doctor_id || null,
        date:       f.date || null,
        cost:       Number(f.cost) || 0,
        status:     f.status === "ongoing" ? "in_progress" : f.status,
        notes:      f.notes || null,
      });
      // Auto-book a follow-up if a next-visit was captured — reuses the
      // historical=true bypass so we don't collide with today's schedule.
      if (f.next_visit) {
        try {
          await api.post("/appointments", {
            patient_id: patient.id,
            doctor_id:  f.doctor_id || null,
            start:      `${f.next_visit}T10:00:00`,
            duration_min: 30,
            procedure:  `Follow-up: ${f.procedure.trim()}`,
            status:     "scheduled",
            historical: true,
          });
        } catch { /* non-fatal — treatment saved */ }
      }
      toast.success("Treatment saved");
      setF({ ...f, date: "", procedure: "", tooth: "", diagnosis: "", cost: 0, notes: "", next_visit: "" });
      onSaved();
    } catch (e) { toast.error(apiError(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="dm-treatment-form">
      <SectionTitle title="Add treatment" hint="Adds to patient's treatment history + timeline" />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Treatment date"><DateDMYInput className="input" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="dm-tx-date" /></Field>
        <Field label="Treating doctor">
          <select className="input" value={f.doctor_id} onChange={(e) => setF({ ...f, doctor_id: e.target.value })} data-testid="dm-tx-doctor">
            <option value="">— Any —</option>
            {doctors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </Field>
        <Field label="Treatment"><input className="input" value={f.procedure} onChange={(e) => setF({ ...f, procedure: e.target.value })} placeholder="RCT · Scaling · Extraction" data-testid="dm-tx-procedure" /></Field>
        <Field label="Tooth number(s)"><input className="input" value={f.tooth} onChange={(e) => setF({ ...f, tooth: e.target.value })} placeholder="16, 17" data-testid="dm-tx-tooth" /></Field>
        <Field label="Diagnosis"><input className="input" value={f.diagnosis} onChange={(e) => setF({ ...f, diagnosis: e.target.value })} placeholder="Caries · Fracture · Pulpitis" data-testid="dm-tx-diagnosis" /></Field>
        <Field label="Status">
          <select className="input" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="dm-tx-status">
            <option value="completed">Completed</option>
            <option value="ongoing">Ongoing</option>
          </select>
        </Field>
        <Field label="Cost (₹)"><input type="number" min="0" className="input" value={f.cost} onChange={(e) => setF({ ...f, cost: e.target.value })} data-testid="dm-tx-cost" /></Field>
        <Field label="Next visit"><DateDMYInput className="input" value={f.next_visit} onChange={(e) => setF({ ...f, next_visit: e.target.value })} data-testid="dm-tx-next" /></Field>
      </div>
      <Field label="Notes"><input className="input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="dm-tx-notes" /></Field>
      <SaveButton onClick={submit} saving={saving} testid="dm-tx-save" label="Save treatment" />
    </div>
  );
}

function PrescriptionForm({ patient, onSaved }) {
  const [doctors, setDoctors] = useState([]);
  const [f, setF] = useState({ date: "", doctor_id: "", drug: "", dosage: "", duration: "", instructions: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/team").then(r => setDoctors((r.data || []).filter(t => t.role === "doctor"))).catch(() => {}); }, []);

  const submit = async () => {
    if (!f.drug.trim()) return toast.error("Medicine is required");
    setSaving(true);
    try {
      await api.post(`/patients/${patient.id}/prescriptions`, {
        items: [{
          drug:         f.drug.trim(),
          dosage:       f.dosage || "1-0-1",
          duration:     f.duration || "5 days",
          instructions: f.instructions || "",
        }],
        notes: f.date ? `Backdated to ${f.date}` : null,
      });
      toast.success("Prescription saved");
      setF({ ...f, date: "", drug: "", dosage: "", duration: "", instructions: "" });
      onSaved();
    } catch (e) { toast.error(apiError(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="dm-rx-form">
      <SectionTitle title="Add prescription" hint="One medicine per save — add more with successive Quick-Adds" />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Date"><DateDMYInput className="input" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="dm-rx-date" /></Field>
        <Field label="Doctor">
          <select className="input" value={f.doctor_id} onChange={(e) => setF({ ...f, doctor_id: e.target.value })} data-testid="dm-rx-doctor">
            <option value="">— Any —</option>
            {doctors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </Field>
        <Field label="Medicine"><input className="input" value={f.drug} onChange={(e) => setF({ ...f, drug: e.target.value })} placeholder="Amoxicillin 500mg" data-testid="dm-rx-drug" /></Field>
        <Field label="Dosage"><input className="input" value={f.dosage} onChange={(e) => setF({ ...f, dosage: e.target.value })} placeholder="1-0-1" data-testid="dm-rx-dosage" /></Field>
        <Field label="Duration"><input className="input" value={f.duration} onChange={(e) => setF({ ...f, duration: e.target.value })} placeholder="5 days" data-testid="dm-rx-duration" /></Field>
        <Field label="Instructions"><input className="input" value={f.instructions} onChange={(e) => setF({ ...f, instructions: e.target.value })} placeholder="After food" data-testid="dm-rx-inst" /></Field>
      </div>
      <SaveButton onClick={submit} saving={saving} testid="dm-rx-save" label="Save prescription" />
    </div>
  );
}

function LabCaseForm({ patient, onSaved }) {
  const [labs, setLabs] = useState([]);
  const [f, setF] = useState({
    treatment_type: "PFM Crown", tooth_numbers: "", shade: "", material: "",
    lab_id: "", date_sent: "", expected_delivery: "", priority: "normal",
    status: "sent", delivery_date: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/laboratories").then(r => setLabs(r.data || [])).catch(() => {}); }, []);

  const submit = async () => {
    if (!f.lab_id) return toast.error("Laboratory is required");
    setSaving(true);
    try {
      await api.post("/lab-cases", {
        patient_id: patient.id,
        treatment_type: f.treatment_type.trim(),
        tooth_numbers:  f.tooth_numbers ? f.tooth_numbers.split(",").map(t => t.trim()).filter(Boolean) : [],
        shade:      f.shade || null,
        material:   f.material || null,
        lab_id:     f.lab_id,
        date_sent:  f.date_sent || null,
        expected_delivery: f.expected_delivery || null,
        priority:   f.priority,
        status:     f.status,
        notes:      [f.notes, f.delivery_date && `Delivered ${f.delivery_date}`].filter(Boolean).join(" · ") || null,
      });
      toast.success("Lab case saved");
      setF({ ...f, tooth_numbers: "", date_sent: "", expected_delivery: "", delivery_date: "", notes: "" });
      onSaved();
    } catch (e) { toast.error(apiError(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="dm-lab-form">
      <SectionTitle title="Add lab case" hint="Mirrors the existing Lab Case module — every field lands on the case" />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Treatment"><input className="input" value={f.treatment_type} onChange={(e) => setF({ ...f, treatment_type: e.target.value })} data-testid="dm-lab-tx" /></Field>
        <Field label="Tooth number(s)"><input className="input" value={f.tooth_numbers} onChange={(e) => setF({ ...f, tooth_numbers: e.target.value })} placeholder="11, 21" data-testid="dm-lab-teeth" /></Field>
        <Field label="Shade"><input className="input" value={f.shade} onChange={(e) => setF({ ...f, shade: e.target.value })} placeholder="A2" data-testid="dm-lab-shade" /></Field>
        <Field label="Material">
          <select className="input" value={f.material} onChange={(e) => setF({ ...f, material: e.target.value })} data-testid="dm-lab-material">
            <option value="">— Select —</option>
            {LAB_MATERIALS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </Field>
        <Field label="Laboratory">
          <select className="input" value={f.lab_id} onChange={(e) => setF({ ...f, lab_id: e.target.value })} data-testid="dm-lab-lab">
            <option value="">— Select —</option>
            {labs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </Field>
        <Field label="Date sent"><DateDMYInput className="input" value={f.date_sent} onChange={(e) => setF({ ...f, date_sent: e.target.value })} data-testid="dm-lab-sent" /></Field>
        <Field label="Expected delivery"><DateDMYInput className="input" value={f.expected_delivery} onChange={(e) => setF({ ...f, expected_delivery: e.target.value })} data-testid="dm-lab-expected" /></Field>
        <Field label="Priority">
          <select className="input" value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} data-testid="dm-lab-priority">
            <option value="normal">Normal</option>
            <option value="urgent">Urgent</option>
          </select>
        </Field>
        <Field label="Status">
          <select className="input" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="dm-lab-status">
            <option value="sent">Sent</option>
            <option value="in_progress">In Progress</option>
            <option value="received">Received</option>
            <option value="delivered">Delivered</option>
          </select>
        </Field>
        <Field label="Delivery date"><DateDMYInput className="input" value={f.delivery_date} onChange={(e) => setF({ ...f, delivery_date: e.target.value })} data-testid="dm-lab-delivered" /></Field>
      </div>
      <Field label="Notes"><input className="input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="dm-lab-notes" /></Field>
      <SaveButton onClick={submit} saving={saving} testid="dm-lab-save" label="Save lab case" />
    </div>
  );
}

function BillForm({ patient, onSaved }) {
  const [f, setF] = useState({
    invoice_number: "", date: "", description: "", amount: 0, discount: 0,
    gst_rate: 18, paid: 0, payment_mode: "cash", notes: "",
  });
  const [saving, setSaving] = useState(false);

  const gross = Number(f.amount) || 0;
  const disc  = Number(f.discount) || 0;
  const gst   = Number(f.gst_rate) || 0;
  const net   = Math.max(0, gross - disc);
  const total = Math.round((net + (net * gst / 100)) * 100) / 100;
  const paid  = Number(f.paid) || 0;
  const outstanding = Math.max(0, total - paid);

  const submit = async () => {
    if (gross <= 0) return toast.error("Amount must be > 0");
    if (paid > total) return toast.error("Paid cannot exceed total");
    setSaving(true);
    try {
      await api.post("/invoices", {
        patient_id: patient.id,
        items:      [{ description: f.description.trim() || "General treatment",
                       quantity: 1, unit_cost: gross }],
        discount_type:  disc > 0 ? "fixed" : "none",
        discount_value: disc,
        gst_rate:       gst,
        status:         paid >= total ? "paid" : "pending",
        due_date:       f.date || null,
        notes:          [f.invoice_number && `Legacy Invoice #${f.invoice_number}`,
                         f.payment_mode && `Paid via ${f.payment_mode}`,
                         f.notes].filter(Boolean).join(" · ") || null,
      });
      toast.success("Bill saved");
      setF({ ...f, invoice_number: "", date: "", description: "", amount: 0, discount: 0, paid: 0, notes: "" });
      onSaved();
    } catch (e) { toast.error(apiError(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="dm-bill-form">
      <SectionTitle title="Add bill" hint="GST, discount + payment mode captured in one go" />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Invoice number"><input className="input" value={f.invoice_number} onChange={(e) => setF({ ...f, invoice_number: e.target.value })} placeholder="Legacy #" data-testid="dm-bill-invno" /></Field>
        <Field label="Date"><DateDMYInput className="input" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="dm-bill-date" /></Field>
        <Field label="Treatment / description"><input className="input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="dm-bill-desc" /></Field>
        <Field label="Amount (₹)"><input type="number" min="0" className="input" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="dm-bill-amount" /></Field>
        <Field label="Discount (₹)"><input type="number" min="0" className="input" value={f.discount} onChange={(e) => setF({ ...f, discount: e.target.value })} data-testid="dm-bill-discount" /></Field>
        <Field label="GST (%)"><input type="number" min="0" className="input" value={f.gst_rate} onChange={(e) => setF({ ...f, gst_rate: e.target.value })} data-testid="dm-bill-gst" /></Field>
        <Field label="Paid (₹)"><input type="number" min="0" className="input" value={f.paid} onChange={(e) => setF({ ...f, paid: e.target.value })} data-testid="dm-bill-paid" /></Field>
        <Field label="Payment mode">
          <select className="input" value={f.payment_mode} onChange={(e) => setF({ ...f, payment_mode: e.target.value })} data-testid="dm-bill-mode">
            <option value="cash">Cash</option>
            <option value="upi">UPI</option>
            <option value="card">Card</option>
            <option value="netbanking">Net Banking</option>
            <option value="cheque">Cheque</option>
            <option value="wallet">Wallet</option>
          </select>
        </Field>
      </div>
      <div className="rounded-xl bg-linen/40 px-3 py-2 text-xs text-ink-mid grid grid-cols-3 gap-2">
        <span>Total: <strong>₹{total.toLocaleString("en-IN")}</strong></span>
        <span>Paid: <strong>₹{paid.toLocaleString("en-IN")}</strong></span>
        <span>Outstanding: <strong data-testid="dm-bill-outstanding">₹{outstanding.toLocaleString("en-IN")}</strong></span>
      </div>
      <Field label="Notes"><input className="input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="dm-bill-notes" /></Field>
      <SaveButton onClick={submit} saving={saving} testid="dm-bill-save" label="Save bill" />
    </div>
  );
}

function AppointmentForm({ patient, onSaved }) {
  const [doctors, setDoctors] = useState([]);
  const [chairs, setChairs] = useState([]);
  const [f, setF] = useState({ date: "", time: "10:00", doctor_id: "", chair_id: "", purpose: "Consultation", status: "completed", notes: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/team").then(r => setDoctors((r.data || []).filter(t => t.role === "doctor"))).catch(() => {});
    api.get("/chairs").then(r => setChairs(r.data || [])).catch(() => {});
  }, []);

  const submit = async () => {
    if (!f.date) return toast.error("Date is required");
    setSaving(true);
    try {
      await api.post("/appointments", {
        patient_id: patient.id,
        doctor_id:  f.doctor_id || null,
        chair_id:   f.chair_id || null,
        start:      `${f.date}T${f.time || "10:00"}:00`,
        duration_min: 30,
        procedure:  f.purpose.trim() || "Consultation",
        status:     f.status,
        notes:      f.notes || null,
        historical: true,
      });
      toast.success("Appointment saved");
      setF({ ...f, date: "", notes: "" });
      onSaved();
    } catch (e) { toast.error(apiError(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="dm-appt-form">
      <SectionTitle title="Add historical appointment" hint="Working-hours check skipped — this already happened" />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Date"><DateDMYInput className="input" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="dm-appt-date" /></Field>
        <Field label="Time"><Time24Input className="input" value={f.time} onChange={(v) => setF({ ...f, time: v })} data-testid="dm-appt-time" /></Field>
        <Field label="Doctor">
          <select className="input" value={f.doctor_id} onChange={(e) => setF({ ...f, doctor_id: e.target.value })} data-testid="dm-appt-doctor">
            <option value="">— Any —</option>
            {doctors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </Field>
        <Field label="Chair">
          <select className="input" value={f.chair_id} onChange={(e) => setF({ ...f, chair_id: e.target.value })} data-testid="dm-appt-chair">
            <option value="">— Any —</option>
            {chairs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Purpose"><input className="input" value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} data-testid="dm-appt-purpose" /></Field>
        <Field label="Status">
          <select className="input" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="dm-appt-status">
            <option value="completed">Completed</option>
            <option value="no_show">No Show</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </Field>
      </div>
      <Field label="Remarks"><input className="input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="dm-appt-notes" /></Field>
      <SaveButton onClick={submit} saving={saving} testid="dm-appt-save" label="Save appointment" />
    </div>
  );
}

function DocumentForm({ patient, onSaved }) {
  const [f, setF] = useState({ doc_type: "Prescription", date: "", source: "", notes: "", upload_later: true });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      // Metadata-only — we log the placeholder as a lab-case-note style patient
      // event so it shows up in the timeline. Actual file attaches later from
      // the patient's Documents tab (chunked uploader lives there).
      await api.post("/lab-cases", {
        patient_id: patient.id,
        treatment_type: `Document: ${f.doc_type}`,
        date_sent:  f.date || null,
        status:     "received",
        notes:      [f.source && `Source: ${f.source}`, f.notes, f.upload_later && "(file upload pending)"].filter(Boolean).join(" · ") || null,
      });
      toast.success("Document metadata saved");
      setF({ ...f, date: "", source: "", notes: "" });
      onSaved();
    } catch (e) { toast.error(apiError(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="dm-doc-form">
      <SectionTitle title="Add document" hint="Save metadata now, upload the scan later from patient profile" />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Document type">
          <select className="input" value={f.doc_type} onChange={(e) => setF({ ...f, doc_type: e.target.value })} data-testid="dm-doc-type">
            {["Prescription", "Consent", "Lab Report", "Referral", "ID Proof", "Insurance", "Other"].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Date"><DateDMYInput className="input" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="dm-doc-date" /></Field>
        <Field label="Source"><input className="input" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} placeholder="Referring doctor · Lab · Patient" data-testid="dm-doc-source" /></Field>
        <Field label="Notes"><input className="input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="dm-doc-notes" /></Field>
      </div>
      <label className="text-xs text-ink-mid inline-flex items-center gap-2">
        <input type="checkbox" checked={f.upload_later} onChange={(e) => setF({ ...f, upload_later: e.target.checked })} data-testid="dm-doc-upload-later" />
        Upload actual file later from patient profile
      </label>
      <SaveButton onClick={submit} saving={saving} testid="dm-doc-save" label="Save document" />
    </div>
  );
}
function SectionTitle({ title, hint }) {
  return (
    <div className="mb-1">
      <div className="text-sm font-medium">{title}</div>
      {hint && <div className="text-[11px] text-ink-soft">{hint}</div>}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-ink/80 mb-1.5">{label}</div>
      {children}
    </label>
  );
}

function SaveButton({ onClick, saving, testid, label }) {
  return (
    <div className="flex justify-end pt-1">
      <button onClick={onClick} disabled={saving} data-testid={testid}
              className="inline-flex items-center gap-1.5 rounded-full bg-pine text-linen text-sm font-medium px-5 py-2 hover:bg-pine/90 disabled:opacity-50">
        {saving ? "Saving…" : <>{label} <Check className="w-3.5 h-3.5" /></>}
      </button>
    </div>
  );
}

function FooterNote({ text }) {
  return (
    <div className="text-[11px] text-ink-soft flex items-center gap-1.5">
      <Database className="w-3 h-3" />
      <span>{text}</span>
    </div>
  );
}

function StyleShim() {
  return <style>{`.input{width:100%;border-radius:12px;border:1px solid #E8EAE9;background:#fff;padding:8px 12px;font-size:14px;outline:none;transition:box-shadow .15s,border-color .15s}.input:focus{border-color:#1A362B;box-shadow:0 0 0 3px rgba(26,54,43,.12)}`}</style>;
}
